import React, { useState } from 'react';
import axios from 'axios';
import { Globe, AlertTriangle, Stethoscope, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '../../utils/api';

const API_URL = getApiUrl();

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

const EVENT_LABEL = {
  country_signal_mismatch: 'Device disagrees with resolved country',
  inr_quote_blocked_for_international: 'INR quote blocked (international)',
  usd_shown_to_india_device: 'USD shown on an Indian-clock device',
};

// §C5/§C6/§C8 — device signals, app country events, manual India/International override, doctor clearance.
export const CountrySignals = ({ lead, onChanged }) => {
  const [busy, setBusy] = useState(false);
  // The override response carries the new market, so the badge flips at once instead of
  // waiting for the modal to be reopened.
  const [applied, setApplied] = useState(null);
  const view = applied ? { ...lead, ...applied } : lead;
  const sig = view?.country_signals || {};
  const events = view?.country_events || [];
  const override = view?.country_override;
  // V56 — the market is the LMS's call; 'unknown' is a real state now that OPS never
  // defaults to India or reads the +91 dial code. Staff-only panel, never shown to a learner.
  const bucket = view?.domestic_or_international || (view?.country ? (String(view.country).toLowerCase() === 'india' ? 'domestic' : 'international') : 'unknown');
  const isIndia = bucket === 'domestic';
  const unresolved = bucket === 'unknown' && !override;
  const flags = sig.country_flags || [];

  const setOverride = async (mode) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/leads/${lead.lead_id}/country-override`, { mode }, { withCredentials: true });
      const lms = res.data.lms_sync;
      setApplied({ country: res.data.country, domestic_or_international: res.data.domestic_or_international,
                   country_override: res.data.country_override });
      toast.success(mode === 'clear' ? 'Override cleared' : `Marked as ${mode === 'india' ? 'India (INR)' : 'International (USD)'}${lms === 'ok' ? ' · synced to LMS' : lms === 'not_configured' ? '' : ' · LMS sync pending'}`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update country');
    } finally { setBusy(false); }
  };

  return (
    <div className="md:col-span-2 bg-sky-50 border border-sky-200 rounded-lg p-4" data-testid="country-signals-section">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-sky-700" />
          <h4 className="font-semibold text-sky-900 text-sm">Country &amp; Pricing</h4>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${unresolved ? 'bg-slate-200 text-slate-700' : isIndia ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'}`} data-testid="pricing-bucket-badge">
            {unresolved ? 'Country not resolved · no link yet' : `${view?.country || '—'} · ${isIndia ? 'INR' : 'USD'}`}
          </span>
          {view?.country_needs_review && !override && (
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid="country-needs-review-badge">
              <AlertTriangle className="w-3 h-3" /> Needs review
            </span>
          )}
          {view?.intl_signal_conflict && !override && (
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid="intl-signal-conflict-badge">
              <AlertTriangle className="w-3 h-3" /> Signal conflict
            </span>
          )}
          {typeof lead?.doctor_clearance === 'boolean' && (
            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${lead.doctor_clearance ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`} data-testid="doctor-clearance-badge">
              <Stethoscope className="w-3 h-3" /> {lead.doctor_clearance ? 'Doctor cleared for exercise' : 'No doctor clearance yet'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isIndia || unresolved ? (
            <button type="button" disabled={busy} onClick={() => setOverride('india')} className="btn-secondary text-xs py-1 disabled:opacity-50" data-testid="mark-india-btn">Mark as India</button>
          ) : null}
          {(isIndia || unresolved) && (
            <button type="button" disabled={busy} onClick={() => setOverride('international')} className="btn-secondary text-xs py-1 disabled:opacity-50" data-testid="mark-international-btn">Mark as International</button>
          )}
          {override && (
            <button type="button" disabled={busy} onClick={() => setOverride('clear')} className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" data-testid="clear-country-override-btn"><RotateCcw className="w-3 h-3" /> Clear</button>
          )}
        </div>
      </div>
      {unresolved && (
        <p className="text-xs text-slate-700 mb-2" data-testid="country-unresolved-note">
          Payment links are blocked until the market is known — confirm on the call and mark her India or International. Never infer it from the phone number.
        </p>
      )}
      {flags.length > 0 && (
        <p className="text-xs text-amber-800 mb-2" data-testid="country-flags">Signals: {flags.join(', ')}{sig.country_reason ? ` · ${sig.country_reason}` : ''}</p>
      )}
      {sig.country_votes && (
        <p className="text-xs text-slate-500 mb-2" data-testid="country-votes">
          LMS vote — India {sig.country_votes._in ?? '—'} vs International {sig.country_votes._intl ?? '—'} (internal only, never share with the learner)
        </p>
      )}
      {override && (
        <p className="text-xs text-sky-800 mb-2" data-testid="country-override-note">Manual override → {override.country} · set {fmt(override.at)}</p>
      )}
      {(sig.device_region || sig.device_timezone || sig.resolved_country || view?.country_source) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" data-testid="device-signals">
          <div><span className="text-slate-500">Resolved by</span><div className="font-medium text-slate-800">{view?.country_source || '—'}</div></div>
          <div><span className="text-slate-500">LMS resolved</span><div className="font-medium text-slate-800">{sig.resolved_country || '—'}{sig.country_confidence ? ` (${sig.country_confidence})` : ''}</div></div>
          <div><span className="text-slate-500">Device region</span><div className="font-medium text-slate-800">{sig.device_region || sig.client_region || '—'}</div></div>
          <div><span className="text-slate-500">Device clock</span><div className="font-medium text-slate-800">{sig.device_timezone || sig.client_timezone || '—'}</div></div>
        </div>
      )}
      {events.length > 0 && (
        <ul className="mt-3 space-y-1" data-testid="country-events">
          {[...events].reverse().slice(0, 5).map((ev, i) => (
            <li key={i} className="text-xs text-slate-700 flex justify-between gap-2">
              <span>{EVENT_LABEL[ev.event] || ev.event}{ev.device_timezone ? ` · ${ev.device_timezone}` : ''}</span>
              <span className="text-slate-400 shrink-0">{fmt(ev.at)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
